// Компилятор шаблонов: обходит DOM, находит директивы ng-* и интерполяцию [[ ]],
// связывает их с областями видимости. Порядок и правила как в AngularJS 1.2:
// приоритет директив, ограничители (ng-if, ng-repeat), шаблоны директив.
import { toJson } from './filters';
import { reportError, reportWarning } from './report';

const START = '[[';
const END = ']]';
const SKIP_CONTENT = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT']);
const BOOLEAN_ATTRS = new Set(['multiple', 'selected', 'checked', 'disabled', 'readonly', 'required', 'open']);

export function normalize(name) {
    return name
        .replace(/^((?:x|data)[:\-_])/i, '')
        .replace(/[:\-_]+(.)/g, (match, letter, offset) => (offset ? letter.toUpperCase() : letter));
}

export function denormalize(name) {
    return name.replace(/[A-Z]/g, letter => '-' + letter.toLowerCase());
}

// Разбирает «текст [[ выражение ]] текст». Возвращает null, если выражений нет.
export function createInterpolate(parse) {
    return function interpolate(text) {
        if (typeof text !== 'string' || text.indexOf(START) === -1) {
            return null;
        }
        const parts = [];
        let index = 0;
        while (index < text.length) {
            const start = text.indexOf(START, index);
            const end = start === -1 ? -1 : text.indexOf(END, start + START.length);
            if (start === -1 || end === -1) {
                parts.push(text.slice(index));
                break;
            }
            if (start > index) {
                parts.push(text.slice(index, start));
            }
            parts.push(parse(text.slice(start + START.length, end)));
            index = end + END.length;
        }
        return (scope, locals) => {
            let output = '';
            for (const part of parts) {
                if (typeof part === 'string') {
                    output += part;
                } else {
                    const value = part(scope, locals);
                    if (value != null) {
                        output += typeof value === 'string' ? value : toJson(value);
                    }
                }
            }
            return output;
        };
    };
}

export class Attributes {
    constructor(element, scope) {
        this.$$element = element;
        this.$$scope = scope;
        this.$attr = {};
        this.$$observers = {};
    }

    $set(name, value, writeAttr = true, attrName) {
        const key = attrName || this.$attr[name] || denormalize(name);
        this.$attr[name] = key;
        const element = this.$$element;
        if (BOOLEAN_ATTRS.has(key)) {
            value = Boolean(value);
            try {
                element[key === 'readonly' ? 'readOnly' : key] = value;
            } catch (error) {
                // свойство может быть только для чтения
            }
            value = value ? key : undefined;
        }
        this[name] = value;
        if (writeAttr && element && element.nodeType === 1) {
            if (value === null || value === undefined) {
                element.removeAttribute(key);
            } else {
                element.setAttribute(key, value);
            }
        }
        (this.$$observers[name] || []).forEach(fn => {
            try {
                fn(value);
            } catch (error) {
                reportError(error);
            }
        });
    }

    $observe(name, fn) {
        if (!this.$$observers[name]) {
            this.$$observers[name] = [];
        }
        this.$$observers[name].push(fn);
        if (!this.$$interpolated || !this.$$interpolated.has(name)) {
            // Как в AngularJS: значение без интерполяции передаётся в начале следующей проверки изменений.
            const notify = () => fn(this[name]);
            if (this.$$scope) {
                this.$$scope.$evalAsync(() => notify());
            } else {
                Promise.resolve().then(notify);
            }
        }
        return () => {
            const list = this.$$observers[name];
            const index = list.indexOf(fn);
            if (index >= 0) {
                list.splice(index, 1);
            }
        };
    }
}

// Классы из first, которых нет в second.
function tokenDifference(first, second) {
    const other = String(second || '').split(/\s+/);
    return String(first || '').split(/\s+/).filter(token => token && other.indexOf(token) === -1);
}

// Начало тега элемента для сообщения об ошибке, как startingTag в AngularJS.
function startingTag(element) {
    const html = element.cloneNode(false).outerHTML || '';
    const match = html.match(/^(<[^>]+>)/);
    return match ? match[1] : html;
}

// Ошибка одной директивы не останавливает остальные директивы элемента (как в AngularJS 1.2).
function safeLink(element, fn) {
    try {
        fn();
    } catch (error) {
        reportError(error, startingTag(element));
    }
}

function byPriority(a, b) {
    const diff = b.priority - a.priority;
    if (diff !== 0) {
        return diff;
    }
    if (a.name !== b.name) {
        return a.name < b.name ? -1 : 1;
    }
    return a.index - b.index;
}

export function createCompiler(runtime) {
    const { parse, directives } = runtime;
    const interpolate = createInterpolate(parse);

    // Кеш содержит только имена: директивы могут регистрироваться после создания компилятора.
    const normalizedNames = new Map();
    function normalizeName(name) {
        let result = normalizedNames.get(name);
        if (result === undefined) {
            result = normalize(name);
            if (normalizedNames.size >= 256) {
                normalizedNames.clear();
            }
            normalizedNames.set(name, result);
        }
        return result;
    }

    function needsCollect(element) {
        if (directives.has(normalizeName(element.nodeName.toLowerCase()))) {
            return true;
        }
        const attributes = element.attributes;
        for (let index = 0; index < attributes.length; index++) {
            const attr = attributes[index];
            const name = normalizeName(attr.name);
            if (directives.has(name) || attr.value.indexOf(START) !== -1
                || name === 'ngCloak' || (name === 'class' && attr.value.indexOf('ng-cloak') !== -1)) {
                return true;
            }
        }
        return false;
    }

    function collect(element, attrs, maxPriority) {
        const found = [];
        const add = (name, restrict, index) => {
            const definition = directives.get(name);
            if (!definition) {
                return;
            }
            const allowed = definition.restrict || 'A';
            if (allowed.indexOf(restrict) === -1) {
                return;
            }
            const priority = definition.priority || 0;
            if (maxPriority !== undefined && priority >= maxPriority) {
                return;
            }
            found.push(Object.assign({}, definition, { name, priority, index }));
        };

        add(normalizeName(element.nodeName.toLowerCase()), 'E', -1);
        const interpolated = [];
        Array.from(element.attributes).forEach((attr, index) => {
            const attrName = attr.name;
            const name = normalizeName(attrName);
            attrs.$attr[name] = attrName;
            const value = attr.value;
            attrs[name] = value;
            if (value.indexOf(START) !== -1) {
                interpolated.push({ name, attrName, value });
            }
            add(name, 'A', index);
        });

        if (maxPriority === undefined || maxPriority > 100) {
            interpolated.forEach(({ name, attrName, value }) => {
                const fn = interpolate(value);
                if (!attrs.$$interpolated) {
                    attrs.$$interpolated = new Set();
                }
                attrs.$$interpolated.add(name);
                found.push({
                    name: `$interpolate:${name}`,
                    priority: 100,
                    index: -1,
                    pre(scope) {
                        // Как в AngularJS 1.2: значение нужно другим директивам до связывания, а в DOM оно
                        // попадает при первой проверке изменений. Для class первая запись заменяет весь
                        // атрибут, поэтому классы, добавленные раньше (ng-pristine, ng-valid), пропадают.
                        attrs[name] = fn(scope);
                        scope.$watch(fn, (value, previous) => {
                            if (name === 'class' && value !== previous) {
                                tokenDifference(previous, value).forEach(cls => runtime.animate.removeClass(element, cls));
                                tokenDifference(value, previous).forEach(cls => runtime.animate.addClass(element, cls));
                            } else {
                                attrs.$set(name, value, true, attrName);
                            }
                        });
                    },
                });
            });
        }

        found.sort(byPriority);
        let terminalPriority;
        const result = [];
        for (const directive of found) {
            if (terminalPriority !== undefined && directive.priority < terminalPriority) {
                break;
            }
            result.push(directive);
            if (directive.terminal) {
                terminalPriority = directive.priority;
            }
        }
        return { list: result, terminal: terminalPriority !== undefined };
    }

    // Контроллер другой директивы того же элемента (ngModel, form); '?' — необязательный.
    function resolveRequire(element, require) {
        if (!require) {
            return undefined;
        }
        const name = require.replace('?', '');
        const controller = element.__sdControllers && element.__sdControllers[name];
        if (!controller && require.charAt(0) !== '?') {
            reportWarning(`Директиве нужен контроллер «${name}», он не найден.`);
        }
        return controller;
    }

    function compileChildren(parent, scope) {
        if (SKIP_CONTENT.has(parent.nodeName)) {
            return;
        }
        let child = parent.firstChild;
        while (child) {
            const next = child.nextSibling;
            compileNode(child, scope);
            child = next;
        }
    }

    function compileText(node, scope) {
        const fn = interpolate(node.nodeValue);
        if (!fn) {
            return;
        }
        node.nodeValue = '';
        scope.$watch(fn, value => {
            node.nodeValue = value;
        });
    }

    // Шаблон директивы вставляется до связывания, как в AngularJS. Шаблон по адресу (templateUrl)
    // приходит позже: связывание элемента откладывается до его получения.
    function applyTemplate(definition, element, scope, done) {
        if (definition.template !== undefined) {
            element.innerHTML = definition.template;
            done();
            return;
        }
        runtime.templates(definition.templateUrl).then(content => {
            // Как $q в AngularJS: ответы связываются через общую очередь проверки.
            // Один ответ не запускает отдельный полный обход для каждого клона шаблона.
            scope.$evalAsync(() => {
                element.innerHTML = content;
                done();
            });
        }, error => reportError(error));
    }

    function compileElement(element, scope, maxPriority) {
        // Статическому элементу не нужен Attributes. Дочерние узлы всё равно компилируются.
        if (!needsCollect(element)) {
            compileChildren(element, scope);
            return;
        }
        const attrs = new Attributes(element, scope);
        const { list, terminal } = collect(element, attrs, maxPriority);
        let nodeScope = scope;
        if (list.some(d => d.scope === true)) {
            nodeScope = scope.$new();
        }

        // ng-cloak и класс ng-cloak снимаются при компиляции, как в AngularJS.
        if (element.hasAttribute('ng-cloak')) {
            element.removeAttribute('ng-cloak');
        }
        if (element.classList && element.classList.contains('ng-cloak')) {
            element.classList.remove('ng-cloak');
        }

        if (!list.length) {
            compileChildren(element, nodeScope);
            return;
        }

        const transclusion = list.find(d => d.transclude === 'element');
        if (transclusion) {
            const anchor = document.createComment(` ${transclusion.name}: ${attrs[transclusion.name]} `);
            // Корень клона ng-repeat уже не является серверным DOM; его вставляет внешний блок.
            const hydrate = maxPriority === undefined && transclusion.name === 'ngIf' && element.hasAttribute('data-sd-hydrate');
            // Для следующих включений нужен исходный шаблон, без уже связанных директив и данных.
            const template = hydrate ? element.cloneNode(true) : element;
            if (hydrate) {
                element.parentNode.insertBefore(anchor, element);
            } else {
                element.parentNode.replaceChild(anchor, element);
            }
            // ng-repeat по этой ссылке находит начало своего блока, если элемент заменён.
            element.__sdReplacedBy = anchor;
            const link = (node, childScope, attach) => {
                attach(node);
                compileElement(node, childScope, transclusion.priority);
                return node;
            };
            const transclude = (childScope, attach) => link(template.cloneNode(true), childScope, attach);
            if (hydrate) {
                // ng-if выбирает исходный DOM только при первом true. Первый false удаляет его.
                transclude.hydrate = (childScope, attach) => {
                    if (!childScope) {
                        element.parentNode.removeChild(element);
                        return undefined;
                    }
                    return link(element, childScope, attach);
                };
            }
            list.filter(d => d !== transclusion && d.priority >= transclusion.priority && d.pre)
                .forEach(d => d.pre(nodeScope, anchor, attrs, runtime));
            transclusion.link(nodeScope, anchor, attrs, runtime, transclude);
            return;
        }

        const withTemplate = list.find(d => d.template !== undefined || d.templateUrl);
        const link = () => {
            const controllers = {};
            list.forEach(definition => {
                if (definition.controller) {
                    controllers[definition.name] = definition.controller(nodeScope, element, attrs, runtime);
                }
            });
            if (Object.keys(controllers).length) {
                element.__sdControllers = Object.assign(element.__sdControllers || {}, controllers);
            }

            list.forEach(definition => {
                if (definition.pre) {
                    safeLink(element, () => definition.pre(nodeScope, element, attrs, runtime, resolveRequire(element, definition.require)));
                }
            });

            if (!terminal) {
                compileChildren(element, nodeScope);
            }
            for (let i = list.length - 1; i >= 0; i--) {
                const definition = list[i];
                if (definition.link) {
                    safeLink(element, () => definition.link(nodeScope, element, attrs, runtime, resolveRequire(element, definition.require)));
                }
            }
        };
        if (withTemplate) {
            applyTemplate(withTemplate, element, nodeScope, link);
        } else {
            link();
        }
    }

    function compileNode(node, scope, maxPriority) {
        if (node.nodeType === 3) {
            compileText(node, scope);
        } else if (node.nodeType === 1) {
            try {
                compileElement(node, scope, maxPriority);
            } catch (error) {
                reportError(error, startingTag(node));
            }
        }
    }

    return {
        compile(node, scope) {
            compileNode(node, scope);
        },
    };
}
